import { RowDataUpdatedEvent, SelectionChangedEvent } from "ag-grid-community";
import { CollectionHeaderEntity } from "domain/entity/Collection/CollectionHeaderEntity";
import _ from "lodash";
import { INITIAL_COLLECTION_HDR_COL_DEF, transferRowData } from "presentation/constant/Collection/CollectionColumnDefinition";
import { useCollectionVM } from "presentation/hook/Collection/useCollectionVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useCollectionTracked } from "presentation/store/Collection/CollectionProvider";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "veronica-ui-component/dist/component/core";

const CollectionHdrTablePanel: React.FC = () => {
    const [collectionState] = useCollectionTracked();
    const collectionVM = useCollectionVM();
    const { collectionHdrList, isShowDetailPanel } = collectionState;
    const gridRef: any = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const messageBarVM = useMessageBarVM();
    const [showOverlay, setShowOverlay] = useState<boolean>(false);

    useEffect(() => {
        setShowOverlay(collectionState.isSearchLoading || (!collectionState.isSearchLoading &&
            collectionHdrList && collectionHdrList.length) ? false : true);
    }, [collectionState.isSearchLoading, collectionHdrList, collectionState.isReselectRows]);

    useEffect(() => {
        if (gridRef.current?.gridRef.current &&
            collectionState.isSearchLoading &&
            gridRef.current?.gridRef.current?.api?.getSelectedNodes()?.length > 0) {
            gridRef.current?.gridRef.current?.api?.getSelectedNodes()?.forEach((node: any) => {
                node.setSelected(false);
            });
        }
    }, [collectionState.isSearchLoading]);

    useEffect(() => {
        if (!collectionState.isShowDetailPanel && !_.isEmpty(collectionState.tempSelectedRows)) {
            collectionVM.updateSelectedRows(collectionState.tempSelectedRows);
        }
    }, [collectionState.isShowDetailPanel, collectionState.tempSelectedRows, collectionVM]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        collectionVM.updateSelectedRows(selectedRows);
    }, [collectionVM])

    const onRowDataUpdated = useCallback((params: RowDataUpdatedEvent) => {
        if (collectionState.isReselectRows && !_.isEmpty(collectionState.tempSelectedRows) &&
            !_.isEmpty(collectionHdrList)) {
            params?.api?.forEachNode((node: any) => {
                if (collectionState.tempSelectedRows?.some(header => header.draftInvoiceNo === node?.data?.draftInvoiceNo)) {
                    node.setSelected(true);
                }
            });
        }
    }, [collectionState.isReselectRows, collectionState.tempSelectedRows, collectionHdrList])

    const handleRowDoubleClick = useCallback((entity: CollectionHeaderEntity) => {
        setIsLoading(true);
        collectionVM.onRowDoubleClick(collectionHdrList, entity).then(data => {
            if (data) {
                messageBarVM.showError(data);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        });
        collectionVM.resetDefaultSelectedRows();
    }, [collectionHdrList, collectionVM, messageBarVM])

    const isRowSelectable = useCallback((params: any) => {
        return !isShowDetailPanel;
    }, [isShowDetailPanel]);

    const memoTable = useMemo(() => {
        return (
            <NbisTable
                id='collection-hdr-table'
                isNewColumnSetting={true}
                columns={INITIAL_COLLECTION_HDR_COL_DEF}
                data={transferRowData(collectionHdrList ?? [])}
                // data={transferRowData(collectionHdrList?.map((entity: CollectionHeaderEntity) => ({
                //     ...entity,
                //     flag_edit: collectionState.tempSelectedRows?.some((row: CollectionHeaderEntity) => row.id === entity.id),
                // })))}
                showPaginator={false}
                editable={false}
                showAddIcon={false}
                showDeleteButton={false}
                showReloadIcon={false}
                isScrollHighlighted={true}
                selectionMode={true}
                rowSelection={"multiple"}
                isRowHighligted={true}
                handleSelectionChanged={handleSelectionChange}
                onRowDoubleClick={(e: any, entity: CollectionHeaderEntity) => handleRowDoubleClick(entity)}
                gridHeight="customHeight"
                customHeight="calc(100vh - 125px)"
                ref={gridRef}
                handleRowSelectable={isRowSelectable}
                onRowDataUpdated={(params: RowDataUpdatedEvent) => onRowDataUpdated(params)}
                showDefaultOverlayNoRowsTemplate={showOverlay}
            />
        );
    }, [collectionHdrList, onRowDataUpdated, handleRowDoubleClick, handleSelectionChange, isRowSelectable, showOverlay])

    useEffect(() => {
        if (gridRef.current && gridRef.current.gridRef && gridRef.current.gridRef.current && gridRef.current.gridRef.current.api) {
            gridRef.current.gridRef.current.api.redrawRows();
        }
    }, [isShowDetailPanel])

    return <><TableWrapper>
        {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
        {memoTable}
    </TableWrapper>
    </>;
}

export default memo(CollectionHdrTablePanel);
